/**
 * @fileoverview Time Range Selector
 * @description Preset time range buttons with auto-refresh toggle.
 */

import React from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
  Tooltip,
  IconButton,
  Typography,
} from '@mui/material';
import { RefreshCw, ExternalLink } from 'lucide-react';
import { TIME_RANGES } from '../utils/timeRangeUtils';

const TimeRangeSelector = ({
  timeRange,
  onTimeRangeChange,
  autoRefresh,
  onAutoRefreshToggle,
  onManualRefresh,
  loading,
  resourceLink,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
        mb: 2,
        p: 1.5,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
          Time Range:
        </Typography>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={(_, val) => val && onTimeRangeChange(val)}
          size="small"
        >
          {TIME_RANGES.map((tr) => (
            <ToggleButton
              key={tr.key}
              value={tr.key}
              sx={{
                px: 2,
                textTransform: 'none',
                fontWeight: timeRange === tr.key ? 700 : 400,
              }}
            >
              <Tooltip title={tr.description}>
                <span>{tr.label}</span>
              </Tooltip>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Tooltip title="Refresh now">
          <IconButton
            onClick={onManualRefresh}
            disabled={loading}
            size="small"
            sx={{
              animation: loading ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
            }}
          >
            <RefreshCw size={18} />
          </IconButton>
        </Tooltip>

        <FormControlLabel
          control={
            <Switch
              checked={autoRefresh}
              onChange={(e) => onAutoRefreshToggle(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              Auto-refresh (30s)
            </Typography>
          }
        />

        {resourceLink && (
          <Tooltip title="View in Azure Portal">
            <IconButton
              component="a"
              href={resourceLink}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              color="primary"
            >
              <ExternalLink size={18} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default TimeRangeSelector;
